import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import style from "./index.module.scss";
import WrapperContent from "../../../components/shared/WrapperContent";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import CloseIcon from "../../../components/shared/icons/CloseIcon";
import InputCustom from "../../../components/shared/forms/input-custom";
import AvatarCameraIcon from "../../../components/shared/icons/AvatarCameraIcon";
import { Driver, File, User } from "../../../services";
import { requestErrorDisplay } from "../../../utils";
import InputMask from "react-input-mask";
import { toast } from "react-toastify";
import Loading from "../../../components/shared/Loading/Loading";
import cn from "classnames";
import InviteLinkModal from "../../../components/drivers/InviteLinkModal/InviteLinkModal";

const DriversCreatePage = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const driversUrl = "/drivers";
  const backUrl = searchParams.get("redirectUrl") ? searchParams.get("redirectUrl") : driversUrl;
  const [isLoading, setLoading] = useState(false);
  const [name, setName] = useState("");
  const [surname, setSurname] = useState("");
  const [patronymic, setPatronymic] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [iin, setIin] = useState("");
  const [idNumber, setIdNumber] = useState("");
  const [issuedBy, setIssuedBy] = useState("");
  const [issuedDate, setIssuedDate] = useState("");
  const [carIdPhotoOne, setCarIdPhotoOne] = useState(null);
  const [carIdPhotoTwo, setCarIdPhotoTwo] = useState(null);
  const [userIdPhotoOne, setUserIdPhotoOne] = useState(null);
  const [userIdPhotoTwo, setUserIdPhotoTwo] = useState(null);
  const [phoneError, setPhoneError] = useState("");
  const [issuedDateError, setIssuedDateError] = useState(false);
  const [showPhoneError, setShowPhoneError] = useState(false);
  const [isCheckingPhone, setIsCheckingPhone] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [inviteUrl, setInviteUrl] = useState("");
  const [createdDriverName, setCreatedDriverName] = useState("");
  const [expireLink, setExpireLink] = useState("");

  const [errors, setErrors] = useState({
    name: "",
    surname: "",
    phoneNumber: "",
    idNumber: "",
    iin: "",
    issuedBy: "",
    issuedDate: "",
    carIdPhotoOne: "",
    carIdPhotoTwo: "",
    userIdPhotoOne: "",
    userIdPhotoTwo: "",
  });

  const validateFormatData = () => {
    const newErrors = {
      name: "",
      surname: "",
      phoneNumber: "",
      idNumber: "",
      iin: "",
      issuedBy: "",
      issuedDate: "",
      carIdPhotoOne: "",
      carIdPhotoTwo: "",
      userIdPhotoOne: "",
      userIdPhotoTwo: "",
    };

    let isValid = true;

    if (!name.trim()) {
      newErrors.name = t("driversCreatePage.validation.invalidName");
      isValid = false;
    }

    if (!surname.trim()) {
      newErrors.surname = t("driversCreatePage.validation.invalidLastName");
      isValid = false;
    }

    if (phoneNumber) {
      const cleanPhone = phoneNumber.replace(/\D/g, "");
      if (cleanPhone.length < 8 || cleanPhone.length > 15) {
        newErrors.phoneNumber = t("driversCreatePage.validation.invalidPhone");
        isValid = false;
      }
    }

    if (!idNumber || idNumber.length !== 9) {
      newErrors.idNumber = t("driversCreatePage.validation.invalidIdNumber");
      isValid = false;
    }

    if (iin && iin.length !== 12) {
      newErrors.iin = t("driversCreatePage.validation.invalidIin");
      isValid = false;
    }

    if (!issuedBy.trim()) {
      newErrors.issuedBy = t("driversCreatePage.validation.invalidIssuedBy");
      isValid = false;
    }

    if (!issuedDate || issuedDate.length !== 10) {
      newErrors.issuedDate = t("driversCreatePage.validation.invalidIssuedDate");
      setIssuedDateError(true);
      isValid = false;
    }

    // if (!carIdPhotoOne) {
    //   newErrors.carIdPhotoOne = t("driversCreatePage.validation.invalidIdFrontPhoto");
    //   isValid = false;
    // }

    // if (!carIdPhotoTwo) {
    //   newErrors.carIdPhotoTwo = t("driversCreatePage.validation.invalidIdBackPhoto");
    //   isValid = false;
    // }

    if (!userIdPhotoOne) {
      newErrors.userIdPhotoOne = t("driversCreatePage.validation.invalidIdFrontPhoto");
      isValid = false;
    }

    if (!userIdPhotoTwo) {
      newErrors.userIdPhotoTwo = t("driversCreatePage.validation.invalidIdBackPhoto");
      isValid = false;
    }

    setErrors(newErrors);

    if (!isValid) {
      Object.values(newErrors).forEach((error) => {
        if (error) {
          toast.error(error);
        }
      });
    }

    return isValid;
  };

  const checkPhoneExists = async (phoneToCheck) => {
    if (!phoneToCheck) return { exists: false, error: null };

    const cleanPhone = phoneToCheck.replace(/\D/g, "");
    if (cleanPhone.length < 8 || cleanPhone.length > 15) return { exists: false, error: null };

    setIsCheckingPhone(true);
    setPhoneError("");

    try {
      const response = await User.check("phoneNumber", cleanPhone);

      const exists = response.data?.isExists || false;

      if (exists) {
        const errorMessage = response.data?.errorText || t("driversCreatePage.toast.phoneExists");
        setPhoneError(errorMessage);
        return { exists: true, error: errorMessage };
      }

      return { exists: false, error: null };
    } catch (error) {
      console.error("Error checking phone:", error);
      const errorMessage = t("driversCreatePage.toast.phoneCheckError");
      setPhoneError(errorMessage);
      return { exists: false, error: errorMessage };
    } finally {
      setIsCheckingPhone(false);
    }
  };

  const onSubmit = async () => {
    setShowPhoneError(true);

    if (!validateFormatData()) {
      return false;
    }

    if (phoneNumber) {
      const phoneCheckResult = await checkPhoneExists(phoneNumber);
      if (phoneCheckResult.exists) {
        toast.error(phoneCheckResult.error);
        setErrors((prev) => ({ ...prev, phoneNumber: phoneCheckResult.error }));
        return false;
      }

      if (phoneCheckResult.error) {
        toast.error(phoneCheckResult.error);
        return false;
      }
    }

    setLoading(true);
    const data = {
      name: name.trim(),
      surname: surname.trim(),
      patronymic: patronymic.trim(),
      phoneNumber: phoneNumber.replace(/\D/g, ""),
      iin,
      idNumber,
      issuedDate: formatIssuedDate(issuedDate),
      issuedBy: issuedBy.trim(),
      email: "",
    };

    Driver.createDriverByExpeditor(data)
      .then((res) => res.data)
      .then(async (data) => {
        const driverId = data.driver.id;
        if (carIdPhotoOne) {
          await uploadFile({
            driverId,
            side: 0,
            file: carIdPhotoOne,
            type: 6, // Водительское удостоверение
          });
        }
        if (carIdPhotoTwo) {
          await uploadFile({
            driverId,
            side: 1,
            file: carIdPhotoTwo,
            type: 6, // Водительское удостоверение
          });
        }
        if (userIdPhotoOne) {
          await uploadFile({
            driverId,
            side: 0,
            file: userIdPhotoOne,
            type: 3, // Удостоверение личности
          });
        }
        if (userIdPhotoTwo) {
          await uploadFile({
            driverId,
            side: 1,
            file: userIdPhotoTwo,
            type: 3, // Удостоверение личности
          });
        }
        toast.success(t("driversCreatePage.toast.createSuccess"));

        if (data.url) {
          setInviteUrl(data.url);
          setCreatedDriverName(name.trim());
          setExpireLink(data?.expiry);
          setShowModal(true);
        } else {
          navigate(backUrl);
        }
      })
      .catch((error) => {
        requestErrorDisplay(error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const formatIssuedDate = (date) => {
    const [day, month, year] = date.split("-");
    if (!day || !month || !year) return "";
    return `${year}-${month}-${day}`;
  };

  const uploadFile = (data) => {
    const Formdata = new FormData();
    for (const dataKey in data) {
      Formdata.append(dataKey, data[dataKey]);
    }
    return File.uploadDocumentDriver(Formdata);
  };

  const restrictDateInput = (value, setter) => {
    if (!value) {
      setter("");
      return "";
    }

    setter(value);

    if (issuedDateError && value.length === 10) {
      setIssuedDateError(false);
      setErrors((prev) => ({ ...prev, issuedDate: "" }));
    }

    const numericValue = value.replace(/[^\d-]/g, "");

    const parts = numericValue.split("-");
    let [day, month, year] = ["", "", ""];

    if (parts.length > 0) day = parts[0].replace(/\D/g, "");
    if (parts.length > 1) month = parts[1].replace(/\D/g, "");
    if (parts.length > 2) year = parts[2].replace(/\D/g, "");

    if (day.length === 2) {
      const dayNum = parseInt(day, 10);
      if (dayNum < 1) day = "01";
      if (dayNum > 31) day = "31";
    }

    if (month.length === 2) {
      const monthNum = parseInt(month, 10);
      if (monthNum < 1) month = "01";
      if (monthNum > 12) month = "12";
    }

    if (year.length === 4) {
      const yearNum = parseInt(year, 10);
      if (yearNum < 1900) year = "1900";
    }

    let formattedDate = day;
    if (parts.length > 1) formattedDate += "-" + month;
    if (parts.length > 2) formattedDate += "-" + year;

    setter(formattedDate);
    return formattedDate;
  };

  const handleModalClose = () => {
    setShowModal(false);
    navigate(backUrl);
  };

  return (
    <WrapperContent>
      <div className={style.wrapper}>
        <h1 className={style.title}>{t("driversCreatePage.title")}</h1>
        <div className={style.content}>
          <div className={style.field_col_2}>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.name.label")} *</span>
              <InputCustom
                placeholder={t("driversCreatePage.name.placeholder")}
                defaultValue={name}
                onChangeValue={(value) => {
                  setName(value);
                  if (errors.name) setErrors((prev) => ({ ...prev, name: "" }));
                }}
                className={errors.name ? style.input_error : ""}
                errorText={errors?.name}
              />
            </div>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.lastName.label")} *</span>
              <InputCustom
                placeholder={t("driversCreatePage.lastName.placeholder")}
                defaultValue={surname}
                onChangeValue={(value) => {
                  setSurname(value);
                  if (errors.surname) setErrors((prev) => ({ ...prev, surname: "" }));
                }}
                className={errors.surname ? style.input_error : ""}
                errorText={errors?.surname}
              />
            </div>
          </div>

          <div className={style.field_col_2}>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.phone.label")}</span>
              <InputCustom
                placeholder="+"
                defaultValue={phoneNumber}
                onChangeValue={(value) => {
                  let formattedValue = value;
                  if (!formattedValue.startsWith('+')) {
                    formattedValue = '+' + formattedValue.replace(/\+/g, '');
                  }
                  formattedValue = '+' + formattedValue.substring(1).replace(/\D/g, '');
                  setPhoneNumber(formattedValue);
                  if (errors.phoneNumber) setErrors((prev) => ({ ...prev, phoneNumber: "" }));
                  if (phoneError) setPhoneError("");
                }}
                maxLength={16}
                className={(showPhoneError && phoneError) || errors.phoneNumber ? style.input_error : ""}
                errorText={phoneError || errors.phoneNumber}
              />
            </div>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.patronymic.label")}</span>
              <InputCustom
                placeholder={t("driversCreatePage.patronymic.placeholder")}
                defaultValue={patronymic}
                onChangeValue={setPatronymic}
              />
            </div>
          </div>

          <div className={style.field_col_2}>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.iin.label")}</span>
              <InputCustom
                defaultValue={iin}
                onChangeValue={(value) => {
                  setIin(value);
                  if (errors.iin) setErrors((prev) => ({ ...prev, iin: "" }));
                }}
                placeholder={t("driversCreatePage.iin.placeholder")}
                maxLength={12}
                className={errors.iin ? style.input_error : ""}
                errorText={errors.iin}
              />
            </div>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.idNumber.label")} *</span>
              <InputCustom
                defaultValue={idNumber}
                onChangeValue={(value) => {
                  setIdNumber(value);
                  if (errors.idNumber) setErrors((prev) => ({ ...prev, idNumber: "" }));
                }}
                placeholder={t("driversCreatePage.idNumber.placeholder")}
                maxLength={9}
                className={errors.idNumber ? style.input_error : ""}
                errorText={errors?.idNumber}
              />
            </div>
          </div>

          <div className={style.field_col_2}>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.issuedDate.label")} *</span>
              <InputMask
                value={issuedDate}
                maskChar={null}
                mask="99-99-9999"
                onChange={(event) => restrictDateInput(event.target.value, setIssuedDate)}
                disabled={false}
              >
                {(inputProps) => (
                  <InputCustom
                    {...inputProps}
                    defaultValue={issuedDate}
                    onChangeValue={(value) => restrictDateInput(value, setIssuedDate)}
                    placeholder={t("driversCreatePage.issuedDate.placeholder")}
                    maxLength={10}
                    className={errors.issuedDate ? style.input_error : ""}
                    errorText={errors?.issuedDate}
                  />
                )}
              </InputMask>
            </div>
            <div className={style.field}>
              <span className={style.field_label}>{t("driversCreatePage.issuedBy.label")} *</span>
              <InputCustom
                defaultValue={issuedBy}
                onChangeValue={(value) => {
                  setIssuedBy(value);
                  if (errors.issuedBy) setErrors((prev) => ({ ...prev, issuedBy: "" }));
                }}
                placeholder={t("driversCreatePage.issuedBy.placeholder")}
                className={errors.issuedBy ? style.input_error : ""}
                errorText={errors?.issuedBy}
              />
            </div>
          </div>

          <div className={style.section}>
            <h4 className={style.field_section}>{t("orderInfo.ordersDriverInfo.idCardLabel")}</h4>
            <div className={style.field_col_2}>
              <div className={style.field}>
                <span className={style.field_label}>{t("driversCreatePage.idPhotos.front")} *</span>
                <label
                  className={cn(style.field_upload, { [style.field_upload_error]: errors.userIdPhotoOne })}
                >
                  <AvatarCameraIcon fill={"#C3C5CA"} />
                  <span className={style.file_name}>{userIdPhotoOne?.name || t("driversCreatePage.uploadPhoto")}</span>
                  <input
                    type="file"
                    onChange={(event) => {
                      setUserIdPhotoOne(event.target.files[0]);
                      if (errors.userIdPhotoOne) setErrors((prev) => ({ ...prev, userIdPhotoOne: "" }));
                    }}
                  />
                </label>
                {errors.userIdPhotoOne && <p className={style.error_message}>{errors.userIdPhotoOne}</p>}
              </div>
              <div className={style.field}>
                <span className={style.field_label}>{t("driversCreatePage.idPhotos.back")} *</span>
                <label
                  className={cn(style.field_upload, { [style.field_upload_error]: errors.userIdPhotoTwo })}
                >
                  <AvatarCameraIcon fill={"#C3C5CA"} />
                  <span className={style.file_name}>{userIdPhotoTwo?.name || t("driversCreatePage.uploadPhoto")}</span>
                  <input
                    type="file"
                    onChange={(event) => {
                      setUserIdPhotoTwo(event.target.files[0]);
                      if (errors.userIdPhotoTwo) setErrors((prev) => ({ ...prev, userIdPhotoTwo: "" }));
                    }}
                  />
                </label>
                {errors.userIdPhotoTwo && <p className={style.error_message}>{errors.userIdPhotoTwo}</p>}
              </div>
            </div>
          </div>

          <div className={style.section}>
            <h4 className={style.field_section}>{t("driversCreatePage.idSection")}</h4>
            <div className={style.field_col_2}>
              <div className={style.field}>
                <span className={style.field_label}>
                  {t("driversCreatePage.driverLicensePhotos.front")}
                </span>
                <label
                  className={cn(style.field_upload, { [style.field_upload_error]: errors.carIdPhotoOne })}
                >
                  <AvatarCameraIcon fill={"#C3C5CA"} />
                  <span className={style.file_name}>{carIdPhotoOne?.name || t("driversCreatePage.uploadPhoto")}</span>
                  <input
                    type="file"
                    onChange={(event) => {
                      setCarIdPhotoOne(event.target.files[0]);
                      if (errors.carIdPhotoOne) setErrors((prev) => ({ ...prev, carIdPhotoOne: "" }));
                    }}
                  />
                </label>
                {errors.carIdPhotoOne && <p className={style.error_message}>{errors.carIdPhotoOne}</p>}
              </div>
              <div className={style.field}>
                <span className={style.field_label}>{t("driversCreatePage.driverLicensePhotos.back")}</span>
                <label
                  className={cn(style.field_upload, { [style.field_upload_error]: errors.carIdPhotoTwo })}
                >
                  <AvatarCameraIcon fill={"#C3C5CA"} />
                  <span className={style.file_name}>{carIdPhotoTwo?.name || t("driversCreatePage.uploadPhoto")}</span>
                  <input
                    type="file"
                    onChange={(event) => {
                      setCarIdPhotoTwo(event.target.files[0]);
                      if (errors.carIdPhotoTwo) setErrors((prev) => ({ ...prev, carIdPhotoTwo: "" }));
                    }}
                  />
                </label>
                {errors.carIdPhotoTwo && <p className={style.error_message}>{errors.carIdPhotoTwo}</p>}
              </div>
            </div>
          </div>

          <div className={style.button_container}>
            <Link to={backUrl} className={style.buttonCancel}>
              {t("driversCreatePage.buttons.cancel")}
            </Link>
            <button
              className={cn(style.button_upload, {
                [style.button_upload_loading]: isLoading,
              })}
              onClick={() => onSubmit()}
              disabled={isLoading || isCheckingPhone}
            >
              {!isLoading && <span>{t("modalAddTransportCompany.email.button")}</span>}
              {isLoading && <Loading inBlock marginBottom={0} />}
            </button>
          </div>
        </div>
        <Link to={backUrl} className={style.buttonClose}>
          <CloseIcon />
        </Link>
      </div>

      {isLoading && <Loading />}

      <InviteLinkModal
        isOpen={showModal}
        onClose={handleModalClose}
        inviteUrl={inviteUrl}
        driverName={createdDriverName}
        expiry={expireLink}
      />
    </WrapperContent>
  );
};

export default DriversCreatePage;
